'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Eye, EyeOff, ClipboardList, Copy, Check, ArrowRight, UserCheck, ShieldCheck, Mail, KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type AuthMode = 'login' | 'signup';
type UserRole = 'staff' | 'admin';
type RecoveryStep = 'email' | 'code' | 'newpassword' | 'done';

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupFormValues {
  fullName: string;
  email: string;
  initials: string;
  facility: string;
  role: UserRole;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

const DEMO_CREDENTIALS = [
  { role: 'Staff',         email: 'maria.santos@shiftnotes.care',  password: 'ShiftStaff#2026',   redirect: '/shift-notes-entry' },
  { role: 'Administrator', email: 'denise.okafor@shiftnotes.care', password: 'ShiftAdmin#2026',   redirect: '/admin-notes-dashboard' },
  { role: 'Manager',       email: 'rachel.torres@shiftnotes.care', password: 'ShiftManager#2026', redirect: '/admin-notes-dashboard' },
];

// Simulated registered emails for recovery
const REGISTERED_EMAILS = DEMO_CREDENTIALS.map(c => c.email);
// Simulated recovery code (in production this would be sent via email)
const MOCK_RECOVERY_CODE = '847291';

function ForgotPasswordFlow({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState<RecoveryStep>('email');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailError('');
    if (!recoveryEmail || !/^\S+@\S+\.\S+$/.test(recoveryEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 1000));
    setIsSubmitting(false);
    if (!REGISTERED_EMAILS.includes(recoveryEmail)) {
      // Don't reveal if email exists — show same message for security
    }
    toast.success('If this email is registered, a recovery code has been sent.');
    setStep('code');
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    setCodeError('');
    if (!codeInput.trim()) {
      setCodeError('Please enter the recovery code.');
      return;
    }
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 800));
    setIsSubmitting(false);
    if (codeInput.trim() !== MOCK_RECOVERY_CODE) {
      setCodeError(`Invalid code. (Demo hint: use ${MOCK_RECOVERY_CODE})`);
      return;
    }
    setStep('newpassword');
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError('');
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setIsSubmitting(true);
    // BACKEND INTEGRATION: POST /api/auth/reset-password { email: recoveryEmail, code: codeInput, newPassword }
    await new Promise(r => setTimeout(r, 1000));
    setIsSubmitting(false);
    setStep('done');
  }

  return (
    <div className="fade-in">
      {/* Back button */}
      {step !== 'done' && (
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
        >
          <ArrowLeft size={15} />
          Back to Sign In
        </button>
      )}

      {/* Step: Email */}
      {step === 'email' && (
        <>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Mail size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-700 text-slate-800">Forgot Password?</h2>
              <p className="text-sm text-slate-500">Enter your email to receive a recovery code</p>
            </div>
          </div>
          <form onSubmit={handleEmailSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                Work Email Address
              </label>
              <input
                type="email"
                value={recoveryEmail}
                onChange={e => setRecoveryEmail(e.target.value)}
                placeholder="you@shiftnotes.care"
                autoComplete="email"
                className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  emailError ? 'border-red-300' : 'border-slate-200'
                }`}
              />
              {emailError && <p className="text-xs text-red-600 mt-1">{emailError}</p>}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary py-2.5 text-sm mt-1"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending code...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Send Recovery Code
                  <ArrowRight size={15} />
                </span>
              )}
            </button>
          </form>
        </>
      )}

      {/* Step: Code */}
      {step === 'code' && (
        <>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
              <KeyRound size={20} className="text-violet-600" />
            </div>
            <div>
              <h2 className="text-xl font-700 text-slate-800">Enter Recovery Code</h2>
              <p className="text-sm text-slate-500">Check your email for the 6-digit code</p>
            </div>
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <p className="text-xs text-blue-700">
              A recovery code was sent to <span className="font-600">{recoveryEmail}</span>. Check your inbox and enter the code below.
            </p>
          </div>
          <form onSubmit={handleCodeSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                Recovery Code
              </label>
              <input
                type="text"
                value={codeInput}
                onChange={e => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                maxLength={6}
                className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent font-mono tracking-widest text-center text-lg ${
                  codeError ? 'border-red-300' : 'border-slate-200'
                }`}
              />
              {codeError && <p className="text-xs text-red-600 mt-1">{codeError}</p>}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary py-2.5 text-sm"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Verifying...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Verify Code
                  <ArrowRight size={15} />
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => { setStep('email'); setCodeInput(''); setCodeError(''); }}
              className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              Resend code
            </button>
          </form>
        </>
      )}

      {/* Step: New Password */}
      {step === 'newpassword' && (
        <>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <KeyRound size={20} className="text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-700 text-slate-800">Set New Password</h2>
              <p className="text-sm text-slate-500">Choose a strong password for your account</p>
            </div>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                  className={`w-full px-3 py-2.5 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                    passwordError ? 'border-red-300' : 'border-slate-200'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  autoComplete="new-password"
                  className={`w-full px-3 py-2.5 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                    passwordError ? 'border-red-300' : 'border-slate-200'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            {passwordError && <p className="text-xs text-red-600 font-500">{passwordError}</p>}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary py-2.5 text-sm"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Save New Password
                  <ArrowRight size={15} />
                </span>
              )}
            </button>
          </form>
        </>
      )}

      {/* Step: Done */}
      {step === 'done' && (
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-700 text-slate-800 mb-2">Password Updated!</h2>
          <p className="text-sm text-slate-500 mb-6">
            Your password has been reset successfully. You can now sign in with your new password.
          </p>
          <button
            onClick={onBack}
            className="w-full btn-primary py-2.5 text-sm"
          >
            <span className="flex items-center justify-center gap-2">
              Back to Sign In
              <ArrowRight size={15} />
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function LoginPage() {
  const router = useRouter();
  const [mode, setMode] = useState<AuthMode>('login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>('staff');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const loginForm = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupFormValues>({
    defaultValues: {
      fullName: '', email: '', initials: '', facility: '',
      role: 'staff', password: '', confirmPassword: '', agreeTerms: false,
    },
  });

  const handleCopy = async (text: string, fieldKey: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleAutoFill = (cred: typeof DEMO_CREDENTIALS[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    loginForm.clearErrors();
    toast.info(`Credentials filled for ${cred.role}`);
  };

  const onLogin = async (data: LoginFormValues) => {
    setIsSubmitting(true);
    // BACKEND INTEGRATION: POST /api/auth/login { email, password }
    await new Promise(r => setTimeout(r, 1000));

    const matched = DEMO_CREDENTIALS.find(
      c => c.email === data.email && c.password === data.password
    );

    if (!matched) {
      loginForm.setError('email', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      setIsSubmitting(false);
      return;
    }

    toast.success(`Welcome back! Redirecting to ${matched.role} view...`);
    await new Promise(r => setTimeout(r, 800));
    router.push(matched.redirect);
    setIsSubmitting(false);
  };

  const onSignup = async (data: SignupFormValues) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsSubmitting(true);
    // BACKEND INTEGRATION: POST /api/auth/register { ...data }
    await new Promise(r => setTimeout(r, 1200));
    toast.success('Account created! Pending admin approval.');
    setMode('login');
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex flex-col w-[45%] xl:w-[40%] bg-gradient-to-br from-blue-700 via-blue-600 to-sky-500 p-12 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-16 left-16 w-64 h-64 rounded-full border-2 border-white" />
          <div className="absolute top-32 left-32 w-96 h-96 rounded-full border border-white" />
          <div className="absolute -bottom-16 -right-16 w-80 h-80 rounded-full border-2 border-white" />
          <div className="absolute bottom-32 right-16 w-48 h-48 rounded-full border border-white" />
        </div>

        <div className="relative z-10 flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <AppLogo size={40} />
            <span className="text-white font-700 text-xl tracking-tight">ShiftNotes</span>
          </div>

          {/* Headline */}
          <div className="mt-auto mb-auto">
            <h1 className="text-4xl font-700 text-white leading-tight mt-12">
              Documentation<br />
              <span className="text-sky-200">built for care.</span>
            </h1>
            <p className="text-blue-100 mt-4 text-base leading-relaxed max-w-sm">
              The shift notes platform designed for adult residential care staff — structured, compliant, and simple.
            </p>

            {/* Feature list */}
            <div className="mt-8 space-y-3">
              {[
                { icon: <ClipboardList size={16} />, text: 'Hourly documentation across AM, PM & NOC shifts' },
                { icon: <UserCheck size={16} />,     text: 'Staff and admin role-based access' },
                { icon: <ShieldCheck size={16} />,   text: 'Compliance monitoring & missing note alerts' },
              ].map((item, i) => (
                <div key={`feature-${i}`} className="flex items-center gap-3 text-blue-100">
                  <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Code Legend Preview */}
          <div className="mt-auto">
            <p className="text-xs font-600 text-blue-200 uppercase tracking-widest mb-3">Documentation Codes</p>
            <div className="flex flex-wrap gap-2">
              {[
                { code: 'A',  label: 'Activity' },
                { code: 'H',  label: 'Home' },
                { code: 'B',  label: 'Behavior' },
                { code: 'I',  label: 'Incident' },
                { code: 'M',  label: 'Medication' },
                { code: 'DP', label: 'Day Program' },
                { code: 'WP', label: 'Work Program' },
                { code: 'T',  label: 'Training' },
              ].map((item) => (
                <span key={`legend-${item.code}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/15 rounded-md text-white text-xs font-600">
                  <span className="font-mono">{item.code}</span>
                  <span className="text-blue-200 font-400">{item.label}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-700 text-slate-800 text-lg">ShiftNotes</span>
          </div>

          {/* Forgot Password Flow */}
          {showForgotPassword ? (
            <ForgotPasswordFlow onBack={() => setShowForgotPassword(false)} />
          ) : (
            <>
              {/* Mode toggle */}
              <div className="flex bg-slate-100 rounded-xl p-1 mb-8">
                {(['login', 'signup'] as AuthMode[]).map((m) => (
                  <button
                    key={`mode-${m}`}
                    onClick={() => setMode(m)}
                    className={`flex-1 py-2 rounded-lg text-sm font-600 transition-all duration-200 ${
                      mode === m
                        ? 'bg-white text-slate-800 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {m === 'login' ? 'Sign In' : 'Create Account'}
                  </button>
                ))}
              </div>

              {/* Login Form */}
              {mode === 'login' && (
                <div className="fade-in">
                  <h2 className="text-2xl font-700 text-slate-800 mb-1">Welcome back</h2>
                  <p className="text-sm text-slate-500 mb-6">Sign in to your ShiftNotes account</p>

                  {/* Role selector */}
                  <div className="flex gap-3 mb-6">
                    {(['staff', 'admin'] as UserRole[]).map((role) => (
                      <button
                        key={`role-${role}`}
                        onClick={() => setSelectedRole(role)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-600 transition-all duration-150 ${
                          selectedRole === role
                            ? role === 'admin' ?'border-blue-500 bg-blue-50 text-blue-700' :'border-emerald-500 bg-emerald-50 text-emerald-700' :'border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        {role === 'admin' ? <ShieldCheck size={15} /> : <UserCheck size={15} />}
                        {role === 'admin' ? 'Administrator' : 'Staff'}
                      </button>
                    ))}
                  </div>

                  <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4" noValidate>
                    {/* Email */}
                    <div>
                      <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                        Email Address
                      </label>
                      <input
                        type="email"
                        autoComplete="email"
                        placeholder="you@shiftnotes.care"
                        className={`input-field ${loginForm.formState.errors.email ? 'border-red-300 focus:ring-red-400' : ''}`}
                        {...loginForm.register('email', {
                          required: 'Email is required',
                          pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
                        })}
                      />
                      {loginForm.formState.errors.email && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          {loginForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          placeholder="••••••••••"
                          className={`input-field pr-10 ${loginForm.formState.errors.password ? 'border-red-300 focus:ring-red-400' : ''}`}
                          {...loginForm.register('password', {
                            required: 'Password is required',
                            minLength: { value: 6, message: 'Password must be at least 6 characters' },
                          })}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {loginForm.formState.errors.password && (
                        <p className="text-xs text-red-600 mt-1">
                          {loginForm.formState.errors.password.message}
                        </p>
                      )}
                    </div>

                    {/* Remember me + Forgot */}
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          {...loginForm.register('rememberMe')}
                        />
                        <span className="text-sm text-slate-600">Remember me</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-500 transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary py-2.5 text-base mt-2"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Signing in...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Sign In
                          <ArrowRight size={16} />
                        </span>
                      )}
                    </button>
                  </form>

                  {/* Demo Credentials */}
                  <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-xs font-600 text-slate-500 uppercase tracking-wide mb-3">
                      Demo Accounts — Click to autofill
                    </p>
                    <div className="space-y-2">
                      {DEMO_CREDENTIALS.map((cred) => (
                        <div
                          key={`cred-${cred.role}`}
                          className="flex items-center justify-between gap-2 p-2.5 bg-white rounded-lg border border-slate-200 hover:border-blue-200 hover:bg-blue-50/30 transition-colors cursor-pointer group"
                          onClick={() => handleAutoFill(cred)}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-600 flex-shrink-0 ${
                              cred.role === 'Administrator' || cred.role === 'Manager' ?'bg-blue-100 text-blue-700' :'bg-emerald-100 text-emerald-700'
                            }`}>
                              {cred.role}
                            </span>
                            <span className="text-xs text-slate-600 font-mono truncate">{cred.email}</span>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleCopy(cred.email, `email-${cred.role}`); }}
                              className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                              title="Copy email"
                            >
                              {copiedField === `email-${cred.role}` ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleCopy(cred.password, `pw-${cred.role}`); }}
                              className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                              title="Copy password"
                            >
                              {copiedField === `pw-${cred.role}` ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Signup Form */}
              {mode === 'signup' && (
                <div className="fade-in">
                  <h2 className="text-2xl font-700 text-slate-800 mb-1">Create account</h2>
                  <p className="text-sm text-slate-500 mb-6">Register as a ShiftNotes staff member</p>

                  <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4" noValidate>
                    {/* Role selection */}
                    <div>
                      <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                        Account Type
                      </label>
                      <div className="flex gap-3">
                        {(['staff', 'admin'] as UserRole[]).map((role) => (
                          <button
                            key={`signup-role-${role}`}
                            type="button"
                            onClick={() => signupForm.setValue('role', role)}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border-2 text-sm font-600 transition-all duration-150 ${
                              signupForm.watch('role') === role
                                ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-slate-200 text-slate-500 hover:border-slate-300'
                            }`}
                          >
                            {role === 'admin' ? <ShieldCheck size={14} /> : <UserCheck size={14} />}
                            {role === 'admin' ? 'Admin' : 'Staff'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                        Full Name
                      </label>
                      <input
                        type="text"
                        placeholder="Maria Santos"
                        className={`input-field ${signupForm.formState.errors.fullName ? 'border-red-300' : ''}`}
                        {...signupForm.register('fullName', { required: 'Full name is required' })}
                      />
                      {signupForm.formState.errors.fullName && (
                        <p className="text-xs text-red-600 mt-1">{signupForm.formState.errors.fullName.message}</p>
                      )}
                    </div>

                    {/* Email + Initials row */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                          Work Email
                        </label>
                        <input
                          type="email"
                          placeholder="m.santos@facility.org"
                          className={`input-field ${signupForm.formState.errors.email ? 'border-red-300' : ''}`}
                          {...signupForm.register('email', {
                            required: 'Email is required',
                            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Valid email required' },
                          })}
                        />
                        {signupForm.formState.errors.email && (
                          <p className="text-xs text-red-600 mt-1">{signupForm.formState.errors.email.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                          Initials
                        </label>
                        <input
                          type="text"
                          placeholder="MS"
                          maxLength={3}
                          className={`input-field uppercase font-mono ${signupForm.formState.errors.initials ? 'border-red-300' : ''}`}
                          {...signupForm.register('initials', {
                            required: 'Required',
                            maxLength: { value: 3, message: 'Max 3 chars' },
                          })}
                        />
                        {signupForm.formState.errors.initials && (
                          <p className="text-xs text-red-600 mt-1">{signupForm.formState.errors.initials.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Facility */}
                    <div>
                      <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                        Facility Name
                      </label>
                      <select
                        className="input-field"
                        {...signupForm.register('facility', { required: 'Facility is required' })}
                      >
                        <option value="">Select facility...</option>
                        <option value="Sunrise Home">Sunrise Home</option>
                        <option value="Maple Ridge House">Maple Ridge House</option>
                        <option value="Cedar Glen Residence">Cedar Glen Residence</option>
                      </select>
                      {signupForm.formState.errors.facility && (
                        <p className="text-xs text-red-600 mt-1">{signupForm.formState.errors.facility.message}</p>
                      )}
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Minimum 8 characters"
                          className={`input-field pr-10 ${signupForm.formState.errors.password ? 'border-red-300' : ''}`}
                          {...signupForm.register('password', {
                            required: 'Password is required',
                            minLength: { value: 8, message: 'At least 8 characters required' },
                          })}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {signupForm.formState.errors.password && (
                        <p className="text-xs text-red-600 mt-1">{signupForm.formState.errors.password.message}</p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-xs font-600 text-slate-500 uppercase tracking-wide mb-1.5">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Repeat password"
                          className={`input-field pr-10 ${signupForm.formState.errors.confirmPassword ? 'border-red-300' : ''}`}
                          {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {signupForm.formState.errors.confirmPassword && (
                        <p className="text-xs text-red-600 mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
                      )}
                    </div>

                    {/* Terms */}
                    <div>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          {...signupForm.register('agreeTerms', { required: 'You must agree to continue' })}
                        />
                        <span className="text-sm text-slate-600">
                          I agree to the{' '}
                          <button type="button" className="text-blue-600 hover:underline">Terms of Service</button>
                          {' '}and{' '}
                          <button type="button" className="text-blue-600 hover:underline">Privacy Policy</button>
                        </span>
                      </label>
                      {signupForm.formState.errors.agreeTerms && (
                        <p className="text-xs text-red-600 mt-1">{signupForm.formState.errors.agreeTerms.message}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary py-2.5 text-base mt-2"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Creating account...
                        </span>
                      ) : (
                        'Create Account'
                      )}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}

          <p className="text-center text-xs text-slate-400 mt-8">
            ShiftNotes v2.4 · Adult Residential Care Documentation Platform
          </p>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck size={12} className="text-blue-400" />
            <span>HIPAA Compliant · All sessions are monitored and logged · PHI is protected</span>
          </div>
        </div>
      </div>
    </div>
  );
}